import { create } from "zustand";
import { API } from "@/lib/apiConfig";

const API_BASE = API.judicialTrigger;

/**
 * Judicial Trigger Engine state interface.
 * Manages anti-corruption judicial trigger calculations and outcomes.
 */
export interface JudicialTriggerState {
  /** Conflict score (0-100) */
  conflictScore: number;
  /** Oligarch cluster score (0-100) */
  oligarchClusterScore: number;
  /** Procurement risk score (0-100) */
  procurementRiskScore: number;
  /** Tribunal decision status */
  tribunalDecision: "pending" | "warning" | "fine" | "disqualification";
  /** Integrity risk score (0-100) */
  integrityRisk: number;
  /** Evidence admissibility level */
  evidenceAdmissibility: "low" | "medium" | "high";
  /** Geopolitical weight (0-5) */
  geopoliticalWeight: number;
  /** Derived: Judicial action outcome */
  judicialAction: "MonitorOnly" | "RegionalInvestigation" | "NationalInvestigation" | "EU-Prosecution";
  /** Derived: Trigger severity score */
  triggerSeverity: number;
  /** Derived: Recommended auditor tier (1-3) */
  recommendedAuditorTier: number;
  /** Set conflict score */
  setConflictScore: (score: number) => void;
  /** Set oligarch cluster score */
  setOligarchClusterScore: (score: number) => void;
  /** Set procurement risk score */
  setProcurementRiskScore: (score: number) => void;
  /** Set tribunal decision */
  setTribunalDecision: (decision: "pending" | "warning" | "fine" | "disqualification") => void;
  /** Set integrity risk */
  setIntegrityRisk: (risk: number) => void;
  /** Set evidence admissibility */
  setEvidenceAdmissibility: (admissibility: "low" | "medium" | "high") => void;
  /** Set geopolitical weight */
  setGeopoliticalWeight: (weight: number) => void;
  /** Compute judicial outcome based on current state */
  computeJudicialOutcome: () => Promise<void>;
  /** Export judicial trigger data to JSON */
  exportJudicialTrigger: () => void;
  /** Import judicial trigger data from JSON */
  importJudicialTrigger: (data: {
    conflictScore?: number;
    oligarchClusterScore?: number;
    procurementRiskScore?: number;
    tribunalDecision?: "pending" | "warning" | "fine" | "disqualification";
    integrityRisk?: number;
    evidenceAdmissibility?: "low" | "medium" | "high";
    geopoliticalWeight?: number;
  }) => void;
}

/**
 * Calculate trigger severity based on state values.
 */
function calculateTriggerSeverity(
  conflictScore: number,
  oligarchClusterScore: number,
  procurementRiskScore: number,
  integrityRisk: number,
  geopoliticalWeight: number,
  evidenceAdmissibility: "low" | "medium" | "high",
  tribunalDecision: "pending" | "warning" | "fine" | "disqualification"
): number {
  // Base calculation
  let severity =
    conflictScore * 0.3 +
    oligarchClusterScore * 0.2 +
    procurementRiskScore * 0.2 +
    integrityRisk * 0.2 +
    geopoliticalWeight * 20; // Scale 0-5 to 0-100

  // Apply downgrade for low evidence admissibility
  if (evidenceAdmissibility === "low") {
    severity -= 5;
  }

  // Apply upgrade for disqualification
  if (tribunalDecision === "disqualification") {
    severity += 10;
  }

  return Math.max(0, Math.min(100, severity));
}

/**
 * Determine judicial action based on trigger severity.
 */
function determineJudicialAction(triggerSeverity: number): "MonitorOnly" | "RegionalInvestigation" | "NationalInvestigation" | "EU-Prosecution" {
  if (triggerSeverity < 40) {
    return "MonitorOnly";
  } else if (triggerSeverity < 60) {
    return "RegionalInvestigation";
  } else if (triggerSeverity < 75) {
    return "NationalInvestigation";
  } else {
    return "EU-Prosecution";
  }
}

/**
 * Calculate recommended auditor tier (1-3).
 */
function calculateRecommendedAuditorTier(triggerSeverity: number): number {
  return Math.ceil(triggerSeverity / 33);
}

/**
 * Zustand store for Judicial Trigger Engine.
 */
export const useJudicialTriggerStore = create<JudicialTriggerState>((set, get) => ({
  conflictScore: 0,
  oligarchClusterScore: 0,
  procurementRiskScore: 0,
  tribunalDecision: "pending",
  integrityRisk: 0,
  evidenceAdmissibility: "medium",
  geopoliticalWeight: 0,
  judicialAction: "MonitorOnly",
  triggerSeverity: 0,
  recommendedAuditorTier: 1,

  setConflictScore: (score) =>
    set({ conflictScore: Math.max(0, Math.min(100, score)) }),

  setOligarchClusterScore: (score) =>
    set({ oligarchClusterScore: Math.max(0, Math.min(100, score)) }),

  setProcurementRiskScore: (score) =>
    set({ procurementRiskScore: Math.max(0, Math.min(100, score)) }),

  setTribunalDecision: (decision) => set({ tribunalDecision: decision }),

  setIntegrityRisk: (risk) =>
    set({ integrityRisk: Math.max(0, Math.min(100, risk)) }),

  setEvidenceAdmissibility: (admissibility) =>
    set({ evidenceAdmissibility: admissibility }),

  setGeopoliticalWeight: (weight) =>
    set({ geopoliticalWeight: Math.max(0, Math.min(5, weight)) }),

  computeJudicialOutcome: async () => {
    const state = get();
    
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/compute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conflictScore: state.conflictScore,
          oligarchClusterScore: state.oligarchClusterScore,
          procurementRiskScore: state.procurementRiskScore,
          tribunalDecision: state.tribunalDecision,
          integrityRisk: state.integrityRisk,
          evidenceAdmissibility: state.evidenceAdmissibility,
          geopoliticalWeight: state.geopoliticalWeight,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update state from backend response
        set({
          triggerSeverity: data.state.triggerSeverity,
          judicialAction: data.state.judicialAction,
          recommendedAuditorTier: data.state.recommendedAuditorTier,
        });
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local computation only");
    }
    
    // Fallback to local computation
    const triggerSeverity = calculateTriggerSeverity(
      state.conflictScore,
      state.oligarchClusterScore,
      state.procurementRiskScore,
      state.integrityRisk,
      state.geopoliticalWeight,
      state.evidenceAdmissibility,
      state.tribunalDecision
    );

    const judicialAction = determineJudicialAction(triggerSeverity);
    const recommendedAuditorTier = calculateRecommendedAuditorTier(triggerSeverity);

    set({
      triggerSeverity,
      judicialAction,
      recommendedAuditorTier,
    });
  },

  exportJudicialTrigger: () => {
    const state = get();
    const data = {
      conflictScore: state.conflictScore,
      oligarchClusterScore: state.oligarchClusterScore,
      procurementRiskScore: state.procurementRiskScore,
      evidenceAdmissibility: state.evidenceAdmissibility,
      judicialAction: state.judicialAction,
      triggerSeverity: state.triggerSeverity,
      recommendedAuditorTier: state.recommendedAuditorTier,
      geopoliticalWeight: state.geopoliticalWeight,
    };

    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const filename = `judicialTrigger_${dateStr}.json`;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  importJudicialTrigger: (data) => {
    set((state) => ({
      ...state,
      conflictScore: data.conflictScore ?? state.conflictScore,
      oligarchClusterScore: data.oligarchClusterScore ?? state.oligarchClusterScore,
      procurementRiskScore: data.procurementRiskScore ?? state.procurementRiskScore,
      tribunalDecision: data.tribunalDecision ?? state.tribunalDecision,
      integrityRisk: data.integrityRisk ?? state.integrityRisk,
      evidenceAdmissibility: data.evidenceAdmissibility ?? state.evidenceAdmissibility,
      geopoliticalWeight: data.geopoliticalWeight ?? state.geopoliticalWeight,
    }));
    // Recompute after import
    get().computeJudicialOutcome();
  },
}));



